import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
    IKPIDataParams,
    KPIData,
    KPI,
    KPIParams,
    KPIBandParams,
    KPIBand,
    ClientKPIConfigModel,
    BandInfo,
    ClientKPIConfigWebModel,
    MilestoneStaffingMap,
    KPISparklineData,
    PositionMap,
    IKPISparklineData,
} from '../interfaces/kpi-data';
import { ReusableFunctions } from '../classes/reusable-functions';
import { IScorecardKPIInfo } from '../../settings/components/kpi-scorecard/kpi-scorecard-models';

@Injectable({
    providedIn: 'root',
})
export class KPIService {
    private serverUrl = 'https://' + window.location.host; // TODO Get url and version number from appsettings.
    private version = '0.5';
    private _url = (this.serverUrl + '/api/v{version}/kpi').replace('{version}', this.version);

    constructor(private http: HttpClient, private reusableFunctions: ReusableFunctions) {}

    getKPIMasterList(): Observable<IScorecardKPIInfo[]> {
        return this.http.get<IScorecardKPIInfo[]>(this._url + '/GetKPIMasterList');
    }

    getSparklineDataEmployee(KPIDataParams: IKPIDataParams): Observable<KPISparklineData[]> {
        return this.http.post<KPISparklineData[]>(
            this._url + '/DisplayEmployeeSparklineCard',
            KPIDataParams,
            {
                headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
            }
        );
    }

    getKPIMilestoneStaffingKeyMap(): Observable<MilestoneStaffingMap[]> {
        return this.http.get<MilestoneStaffingMap[]>(this._url + '/getKPIMilestoneStaffingKeyMap');
    }

    getKPIAssignedCounts(): Observable<PositionMap[]> {
        return this.http.get<PositionMap[]>(this._url + '/getKPIPositionMap');
    }

    cleanupIncompleteClientKPIs(clientKPIKey: number) {
        const params = new HttpParams().set('clientKPIKey', clientKPIKey.toString());

        this.http
            .delete<any>(this._url + '/DeleteIncompleteClientKPIs', {
                params: params,
            })
            .subscribe((response) => {
                //This should not happen in the future. KPIs should never be incomplete if configured from the UI.
            });
    }

    getData(parameters: IKPIDataParams): Observable<KPIData[]> {
        return this.http.post<KPIData[]>(this._url + '/DisplayLOKPICard', parameters, {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        });
    }

    getKPIScorecardSelection(kpiDataParams: IKPIDataParams): Observable<KPI[]> {
        //const params = new HttpParams().set('paramsString', JSON.stringify(kpiDataParams));
        //return this.http.get<KPI[]>(this._url + '/DisplayKPIScorecardSelection', {
        //    params: params,
        //});

        return this.http.post<KPI[]>(this._url + '/DisplayKPIScorecardSelection', kpiDataParams, {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        });
    }

    getSparklineData(kpiDataParams: IKPIDataParams): Observable<IKPISparklineData[]> {
        return this.http.post<IKPISparklineData[]>(
            this._url + '/DisplayPositionSparklineCard',
            kpiDataParams,
            {
                headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
            }
        );
    }

    getCurrentKPIConfiguration(): Observable<ClientKPIConfigWebModel> {
        let webModel = this.http.get<ClientKPIConfigWebModel>(
            this._url + '/GetClientKPIConfiguration'
        );
        return webModel;
    }

    submitClientKPIConfiguration(clientKPIConfigModel: ClientKPIConfigModel): Observable<any> {
        let clientKPIConfigDataModel: JSON =
            this.buildClientKPIConfigWebModel(clientKPIConfigModel);
        return new Observable((subscriber) => {
            this.http.post<any>(this._url, clientKPIConfigDataModel).subscribe(
                () => {
                    subscriber.next(true);
                },
                (response) => {
                    subscriber.error(response);
                }
            );
        });
    }

    buildClientKPIConfigWebModel(clientKPIConfigModel: ClientKPIConfigModel): any {
        return {
            clientKPIData: this.getClientKPIDataArrayFormatted(clientKPIConfigModel.kpiInfo),
        };
    }

    getClientKPIDataArrayFormatted(kpiInfo: IScorecardKPIInfo[]): any {
        let clientKPIDataArray = [];
        kpiInfo.forEach((kpi) => {
            let clientKPIInfo = {
                clientKPIKey: kpi.clientKPIKey,
                kpiMasterKey: kpi.kpiMasterKey,
                kpiMilestoneKey: kpi.kpiMilestoneKey,
                kpiMilestoneStaffingKey: kpi.kpiMilestoneStaffingKey,
                clientKPIDescription: kpi.kpiDescription,
                target: kpi.bandInfo.target,
                activeDate: this.reusableFunctions.getCurrentDateTimeOffset(),
                inActiveDate: null,
                isActive: true,
                isDeleted: kpi.delete,
                performanceBands: this.getPerformanceBands(kpi.bandInfo),
                isWeightedByLA: kpi.isWeightedByLA,
                isWeightedByLADisabled: kpi.isWeightedByLADisabled,
            };
            clientKPIDataArray.push(clientKPIInfo);
        });
        return clientKPIDataArray;
    }

    getPerformanceBands(bandInfo: BandInfo): any {
        return [
            {
                clientKPIBandKey: -1,
                kpiBandKey: 1,
                rangeStart: null,
                rangeEnd: bandInfo.unacceptableStart,
            },
            {
                clientKPIBandKey: -1,
                kpiBandKey: 2,
                rangeStart: bandInfo.belowAverageStart,
                rangeEnd: bandInfo.belowAverageEnd,
            },
            {
                clientKPIBandKey: -1,
                kpiBandKey: 3,
                rangeStart: bandInfo.acceptableStart,
                rangeEnd: bandInfo.acceptableEnd,
            },
            {
                clientKPIBandKey: -1,
                kpiBandKey: 4,
                rangeStart: bandInfo.aboveAverageStart,
                rangeEnd: bandInfo.aboveAverageEnd,
            },
            {
                clientKPIBandKey: -1,
                kpiBandKey: 5,
                rangeStart: bandInfo.exceptionalStart,
                rangeEnd: null,
            },
        ];
    }

    getKPIs(kpiParams: KPIParams): Observable<KPI[]> {
        const params = new HttpParams().set('positionAbbv', kpiParams.positionAbbv);

        return this.http.get<KPI[]>(this._url + '/DisplayKPISelection', {
            params: params,
        });
    }

    getBands(kpiBandParams: KPIBandParams): Observable<KPIBand[]> {
        const params = new HttpParams()
            .set('fromDatePeriod', kpiBandParams.fromDatePeriod)
            .set('kpiMatrixKey', kpiBandParams.kpiMatrixKey.toString());

        return this.http.get<KPIBand[]>(this._url + '/DisplayKPIBandSelection', {
            params: params,
        });
    }

    setScorecardInfoTableData(data: IScorecardKPIInfo[], kpiScoreCardKey: number = null) {
        let rangeSequence = ['poor', 'fair', 'good', 'veryGood', 'excellent'];

        data.forEach((kpiInf) => {
            kpiInf.kpiScorecardKey = kpiScoreCardKey;
            let startSign = '<=';
            let endSign = '>=';

            if (kpiInf.bestScore === 'Lowest') {
                startSign = '>=';
                endSign = '<=';
            }
            let precision = kpiInf.precision;

            let prefix = '';
            let sufix = '';
            if (kpiInf.isPercentage) {
                sufix = '%';
            } else if (kpiInf.kpiFormat === 'currency') {
                prefix = '$';
            }

            if (kpiInf.scorecardWeight === undefined || kpiInf.scorecardWeight === null) {
                kpiInf.scorecardWeight = 0;
            }

            rangeSequence.forEach((seq, index) => {
                let startVal;
                let endVal;

                let value = '';

                if (index === 0) {
                    endVal = this.reusableFunctions.formatNumber(
                        kpiInf['range' + (index + 1) + 'End'],
                        precision
                    );
                    endVal = prefix + endVal + sufix;
                    value = startSign + ' ' + endVal;
                } else if (index === 4) {
                    startVal = this.reusableFunctions.formatNumber(
                        kpiInf['range' + (index + 1) + 'Start'],
                        precision
                    );
                    startVal = prefix + startVal + sufix;
                    value = startVal + ' ' + endSign;
                } else {
                    startVal = this.reusableFunctions.formatNumber(
                        kpiInf['range' + (index + 1) + 'Start'],
                        precision
                    );
                    endVal = this.reusableFunctions.formatNumber(
                        kpiInf['range' + (index + 1) + 'End'],
                        precision
                    );
                    value = prefix + startVal + sufix + ' - ' + prefix + endVal + sufix;
                }

                kpiInf[seq] = value;
            });

            if (kpiInf.target) {
                let target = this.reusableFunctions.formatNumber(kpiInf.target, precision);
                kpiInf.targetFormatted = prefix + target + sufix;
            }
        });

        return data;
    }
}
